// Generated A4 product datasheets (same layout as v2.0.41) and Settings → Product Datasheets.
import { printDocument } from "../pdflayout.js";
import { isAdminActor } from "../../../shared/access.js";
import html2canvas from "html2canvas";
import { ApiError, closeModal, esc, formatDateTime, get, openModal, post, put, readFileAsDataUrl, toast, orgToday } from "../lib.js";
import { jpegPdf, zip } from "../pdfzip.js";
import { onAction, onForm, route, state } from "../state.js";
import { backBar, errorPanel, loadingPanel, renderShell, setContent } from "../shell.js";
import { DATASHEET_COLOURS, DATASHEET_FONTS, DATASHEET_PAGE2_MAX_BLOCKS, DATASHEET_SPACERS, normalizeDatasheetSettings, type DatasheetBlock, type DatasheetSettings } from "../../../shared/items.js";
import { canViewColumn } from "../../../shared/permissions.js";

export interface SheetData {
  settings: DatasheetSettings;
  specs: Array<[string, string]>;
  product: { id: string; code: string; name: string; brand: string; imageFileId: string | null };
  brand: { name: string; logoFileId: string | null; footerText: string; primaryColor: string; secondaryColor: string };
  primary: string; secondary: string; footerText: string;
  features: string[];
  care: Array<{ name: string; iconFileId: string | null; textOnly?: boolean }>;
  colours: Array<{ name: string; pantone: string; hex: string; imageFileId: string | null }>;
  sizes: string[];
  standards: Array<{ name: string; iconFileId: string | null }>;
  organisation: string;
}

const fileOf = (id: string | null) => (id ? `/api/org/files/${id}` : "");
/** Pictures inside Falcon CRM come from its own address (CRM-only people can't open Falcon PLM's files). */
export const crmFile = (id: string | null) => (id ? `/api/org/crm/files/${id}` : "");
const file = fileOf;
const hex = (v: string, d: string) => (/^#[0-9a-f]{6}$/i.test(v || "") ? v : d);
const label = (t: string) => { const x = String(t || "").trim().toLowerCase(); return x ? x[0].toUpperCase() + x.slice(1) : ""; };

/** What both pages share: the colours, sizes and spacing, the brand logo and the footer wording. */
function sheetParts(d: SheetData, s: DatasheetSettings, file: (id: string | null) => string) {
  const primary = s.themeMode === "brand" ? hex(d.brand.primaryColor, s.primaryColor) : s.primaryColor;
  const secondary = s.themeMode === "brand" ? hex(d.brand.secondaryColor, s.secondaryColor) : s.secondaryColor;
  const footer = s.footerText || d.brand.footerText || "BUILT FOR PERFORMANCE";
  const logo = d.brand.logoFileId ? `<img src="${file(d.brand.logoFileId)}" alt="${esc(d.brand.name)}">` : `<strong>${esc(d.brand.name || d.product.brand)}</strong>`;
  const vars = [
    ["--ds-primary", primary], ["--ds-secondary", secondary], ["--ds-product-name-font", `${s.productNameFontSize}pt`], ["--ds-heading-font", `${s.headingFontSize}pt`],
    ["--ds-body-font", `${s.bodyFontSize}pt`], ["--ds-footer-font", `${s.footerFontSize}pt`], ["--ds-specifications-spacer", `${s.specificationsSpacer}mm`],
    ["--ds-features-spacer", `${s.productFeaturesSpacer}mm`], ["--ds-care-spacer", `${s.careInstructionsSpacer}mm`], ["--ds-colours-spacer", `${s.coloursSpacer}mm`],
    ["--ds-sizes-spacer", `${s.availableSizesSpacer}mm`], ["--ds-section-header", s.sectionHeaderColor], ["--ds-section-bg", s.sectionBackgroundColor], ["--ds-section-border", s.sectionBorderColor],
  ].map(([k, v]) => `${k}:${v}`).join(";");
  const header = `<header class="gds-header"><div class="gds-title"><h1>${esc(d.product.name || "Product Datasheet")}</h1><span>${esc(d.product.code)}</span></div><div class="gds-brand">${logo}</div></header>`;
  const foot = `<footer class="gds-footer"><div class="gds-footer-brand">${logo}</div><p>${esc(footer)}</p><span>${esc(d.brand.name || d.product.brand || d.organisation)}</span></footer>`;
  return { vars, header, foot };
}

/** The datasheet itself (A4, 210 × 297 mm). `s` can be unsaved settings from the settings screen. */
export function datasheetHtml(d: SheetData, s: DatasheetSettings = d.settings, fileUrl: (id: string | null) => string = fileOf): string {
  const file = fileUrl;
  const { vars, header, foot } = sheetParts(d, s, file);
  return `<article class="generated-product-datasheet" style="${esc(vars)}">
  ${header}
  <section class="gds-specs gds-section-box"><h2>Fabric Specifications</h2><div class="gds-spec-grid">${d.specs.map(([l, v]) => `<div><span>${esc(label(l))}:</span> <strong>${esc(v)}</strong></div>`).join("")}</div></section>
  <section class="gds-main">
    <div class="gds-product-image">${d.product.imageFileId ? `<img src="${file(d.product.imageFileId)}" alt="${esc(d.product.name)}">` : "<span>Product image</span>"}</div>
    <div class="gds-product-info">
      <section class="gds-info-section gds-info-features"><h2>Product Features</h2><ul>${d.features.map((x) => `<li>${esc(x)}</li>`).join("") || "<li>Product features can be added on the garment's Details tab.</li>"}</ul></section>
      <section class="gds-info-section gds-info-care"><h2>Care Instructions</h2><div class="gds-care-list">${d.care.map((c) => `<div class="gds-care-row">${c.iconFileId ? `<img src="${file(c.iconFileId)}" alt="">` : c.textOnly ? "" : '<span class="gds-care-placeholder">CARE</span>'}<span>${esc(c.name)}</span></div>`).join("") || '<span class="gds-empty">No wash care selected</span>'}</div></section>
      <section class="gds-info-section gds-info-colours"><h2>Colours</h2><div class="gds-colours">${d.colours.map((c) => `<div>${c.imageFileId ? `<img src="${file(c.imageFileId)}" alt="">` : `<i style="background:${esc(hex(c.hex, "#d1d5db"))}"></i>`}<span>${esc(c.name)}${c.pantone ? `<small>${esc(c.pantone)}</small>` : ""}</span></div>`).join("") || '<span class="gds-empty">No colours selected.</span>'}</div></section>
      <section class="gds-info-section gds-info-sizes"><h2>Available Sizes</h2><div class="gds-sizes">${d.sizes.map((x) => `<span>${esc(x)}</span>`).join("") || "<span>—</span>"}</div></section>
    </div>
  </section>
  <section class="gds-standards gds-section-box"><h2>Standards &amp; Certifications</h2><div class="gds-standards-grid">${d.standards.map((x) => `<div>${x.iconFileId ? `<img src="${file(x.iconFileId)}" alt="">` : '<span class="gds-icon-placeholder">STD</span>'}<strong>${esc(x.name)}</strong></div>`).join("") || '<span class="gds-empty">No standards selected.</span>'}</div></section>
  ${foot}
</article>`;
}

/** The writing on the second page: a blank line starts a new paragraph, a line starting with "-" is a bullet point and
 *  **two stars** make words bold. */
export function page2Text(t: string): string {
  const inline = (x: string) => esc(x).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  const out: string[] = [];
  let para: string[] = [], list: string[] = [];
  const flush = () => {
    if (para.length) out.push(`<p>${para.join("<br>")}</p>`);
    if (list.length) out.push(`<ul>${list.map((x) => `<li>${x}</li>`).join("")}</ul>`);
    para = []; list = [];
  };
  for (const raw of String(t || "").split("\n")) {
    const line = raw.trim();
    const bullet = /^[-•*]\s+(.*)$/.exec(line);
    if (!line) flush();
    else if (bullet) { if (para.length) { out.push(`<p>${para.join("<br>")}</p>`); para = []; } list.push(inline(bullet[1])); }
    else { if (list.length) { out.push(`<ul>${list.map((x) => `<li>${x}</li>`).join("")}</ul>`); list = []; } para.push(inline(line)); }
  }
  flush();
  return out.join("");
}

/** The optional second page: the same header and footer as page 1; between them the pictures and text boxes from
 *  Settings, each where it was placed and at the size it was given. `edit` adds the move and resize handles
 *  (Settings preview only). */
export function datasheetPage2Html(d: SheetData, s: DatasheetSettings = d.settings, fileUrl: (id: string | null) => string = fileOf, edit = false): string {
  const p2 = s.page2;
  if (!p2?.enabled || !p2.blocks?.length) return "";
  const { vars, header, foot } = sheetParts(d, s, fileUrl);
  const blocks = p2.blocks.map((b, i) => {
    const place = `left:${b.x}%;top:${b.y}%;width:${b.w}%;height:${b.h}%`;
    const tools = edit ? `<i class="gds-p2-handle" data-p2-resize="${i}" title="Drag to resize" aria-hidden="true"></i>` : "";
    const attrs = `class="gds-p2-part ${b.type === "text" ? "gds-p2-text" : "gds-p2-img"}${edit ? " gds-p2-edit" : ""}" style="${place}" ${edit ? `data-p2-part="${i}" tabindex="0" title="Drag to move. Drag the corner to resize."` : ""}`;
    if (b.type === "text") return `<section ${attrs}>${b.heading ? `<h2>${esc(b.heading)}</h2>` : ""}<div class="gds-p2-copy">${page2Text(b.text) || (edit ? '<p class="gds-p2-hint">Type the text in the Second page tab.</p>' : "")}</div>${tools}</section>`;
    return `<figure ${attrs}><div class="gds-p2-pic"><img src="${fileUrl(b.fileId)}" alt="${esc(b.caption)}" draggable="false"></div>${b.caption ? `<figcaption>${esc(b.caption)}</figcaption>` : ""}${tools}</figure>`;
  }).join("");
  return `<article class="generated-product-datasheet gds-page2" style="${esc(vars)}">
  ${header}
  <section class="gds-page2-body">${blocks}</section>
  ${foot}
</article>`;
}

/** Every page of the datasheet, one under the other (scroll to see page 2). */
export function datasheetPagesHtml(d: SheetData, s: DatasheetSettings = d.settings, fileUrl: (id: string | null) => string = fileOf, frameClass = "", edit = false): string {
  const p2 = datasheetPage2Html(d, s, fileUrl, edit);
  return `<div class="falcon-sheet-frame ${frameClass}">${datasheetHtml(d, s, fileUrl)}</div>${p2 ? `<div class="falcon-sheet-frame ${frameClass}">${p2}</div>` : ""}`;
}

/** The page sheets of a datasheet as JPEG pages of one PDF (html2canvas draws each A4 page). */
async function sheetPdf(stage: HTMLElement, d: SheetData, fileUrl?: (id: string | null) => string): Promise<Uint8Array> {
  stage.innerHTML = datasheetHtml(d, d.settings, fileUrl) + datasheetPage2Html(d, d.settings, fileUrl);
  const pages: Array<{ jpeg: Uint8Array; width: number; height: number }> = [];
  for (const sheet of Array.from(stage.children) as HTMLElement[]) {
    await waitForImages(sheet);
    const canvas = await html2canvas(sheet, { scale: 2, backgroundColor: "#ffffff", logging: false, width: 794, height: 1123, windowWidth: 1200 });
    const blob = await new Promise<Blob>((r, x) => canvas.toBlob((b) => (b ? r(b) : x(new Error("The datasheet couldn't be drawn."))), "image/jpeg", 0.9));
    pages.push({ jpeg: new Uint8Array(await blob.arrayBuffer()), width: canvas.width, height: canvas.height });
  }
  return jpegPdf(pages);
}

// ---- One garment's datasheet ---------------------------------------------------------------------------
let current: SheetData | null = null;
route("/app/items/:id/datasheet", async ({ params }) => {
  renderShell({ active: "products", title: "Product Datasheet", subtitle: "", content: loadingPanel() });
  try {
    current = await get<SheetData>(`/api/org/items/${params.id}/datasheet`);
  } catch (err) {
    setContent(errorPanel(err instanceof ApiError && err.status === 404 ? "That garment doesn't exist." : (err as Error).message));
    return;
  }
  const d = current;
  renderShell({ active: "products", title: `${d.product.code} · Datasheet`, subtitle: d.product.name, content: "" });
  setContent(`${backBar(`/app/items/${d.product.id}`, `Back to ${d.product.code}`)}
<div class="falcon-sheet-toolbar panel">
  <div><strong>Generated datasheet</strong><span class="muted">Built from the garment, its fabric, the colour, standard and wash-care lists and the brand. It updates whenever those change.${d.settings.page2?.enabled && d.settings.page2.blocks.length ? " Scroll down for page 2." : ""}</span></div>
  <div class="toolbar">${isAdminActor(state.session.actor) || state.session.actor?.permissions.actions.manageDatabase ? '<a class="secondary" href="/app/settings/datasheets" data-link>Datasheet Settings</a>' : ""}<button type="button" class="primary" data-action="datasheet-print">Print / Save PDF</button></div>
</div>
<div class="falcon-sheet-stage">${datasheetPagesHtml(d)}</div>`);
});

onAction("datasheet-print", () => {
  const sheets = Array.from(document.querySelectorAll<HTMLElement>(".falcon-sheet-stage .generated-product-datasheet"));
  if (!sheets.length || !current) return;
  // Each datasheet page is one designed page: kept whole (scaled to the page if needed) inside the shared layout.
  const src = document.createElement("div");
  for (const sheet of sheets) {
    const keep = sheet.cloneNode(true) as HTMLElement;
    keep.classList.add("pdf-keep");
    src.appendChild(keep);
  }
  void post(`/api/org/items/${current.product.id}/datasheet/downloaded`, {}).catch(() => undefined);
  void printDocument(src, { title: `${current.product.code} · Datasheet`, subtitle: current.product.name || "" }, `${current.product.code} Datasheet`);
});

// ---- Settings → Product Datasheets --------------------------------------------------------------------------------
let design: { saved: DatasheetSettings; canEdit: boolean; sample: SheetData | null; brands: string[]; garments: Array<{ id: string; code: string; name: string }> } | null = null;

route("/app/settings/datasheets", async ({ query }) => {
  renderShell({ active: "settings", title: "Product Datasheets", subtitle: "The design of the A4 datasheets generated for every garment.", content: loadingPanel() });
  try {
    const [cfg, lists, items] = await Promise.all([
      get<{ settings: DatasheetSettings; canEdit: boolean }>("/api/org/settings/datasheet"),
      get<{ lists: Record<string, Array<{ value: string }>> }>("/api/org/lists"),
      get<{ items: any[] }>("/api/org/items?type=product"),
    ]);
    const garments = items.items.filter((x) => x.status === "active").map((x) => ({ id: x.id, code: x.code, name: x.name }));
    const pick = garments.find((g) => g.id === query.get("sample")) || garments[0];
    design = { saved: cfg.settings, canEdit: cfg.canEdit, sample: null, brands: (lists.lists.brand || []).map((b) => b.value), garments };
    if (pick) design.sample = await get<SheetData>(`/api/org/items/${pick.id}/datasheet`);
    renderDesign();
  } catch (err) {
    setContent(`${backBar("/app/settings", "Back to Settings")}${errorPanel((err as Error).message)}`);
  }
});

const DS_TABS: Array<[string, string]> = [["brand", "Brand & colours"], ["boxes", "Boxes & fonts"], ["spacing", "Spacing"], ["page2", "Second page"]];
let dsTab = "brand";
/** The second page's parts while they are being changed (saved with the rest of the settings). */
let p2Blocks: DatasheetBlock[] = [];

function renderDesign() {
  if (!design) return;
  const s = design.saved, ro = design.canEdit ? "" : "disabled";
  p2Blocks = s.page2.blocks.map((b) => ({ ...b }));
  const composition = canViewColumn(state.session.actor ?? null, "fabric", "composition");
  const num = (k: keyof DatasheetSettings, lbl: string, min: number, max: number, unit: string) => `<div class="form-group"><label>${esc(lbl)} <span class="muted">${min}–${max} ${unit}</span></label><input type="number" name="${k}" min="${min}" max="${max}" step="0.5" value="${esc(s[k])}" ${ro}></div>`;
  const slider = (k: keyof DatasheetSettings, lbl: string, max: number) => `<div class="ds-slider-row"><label for="ds-${k}">${esc(lbl)}</label><input type="range" min="0" max="${max}" step="0.5" value="${esc(s[k])}" data-sync="${k}" aria-label="${esc(lbl)}" ${ro}><input id="ds-${k}" type="number" name="${k}" min="0" max="${max}" step="0.5" value="${esc(s[k])}" ${ro}><span class="muted">mm</span></div>`;
  const colour = (name: string, lbl: string, v: string) => `<label class="ds-colour"><input type="color" name="${name}" value="${esc(v)}" ${ro}><span>${esc(lbl)}</span></label>`;
  const pane = (k: string, body: string) => `<div class="ds-pane" data-pane="${k}" ${k === dsTab ? "" : "hidden"}>${body}</div>`;
  const p2On = s.page2.enabled && s.page2.blocks.length > 0;
  setContent(`${backBar("/app/settings", "Back to Settings")}
<div class="falcon-ds-layout">
<form class="panel falcon-ds-form" data-form="datasheet-settings" novalidate>
  <div class="panel-head"><div><h3>Product Datasheets</h3><span class="muted">Changes show in the preview straight away. Save to use them for every garment.</span></div></div>
  ${design.canEdit ? "" : '<div class="form-message">You can look at the design. Only Admins can change it.</div>'}
  <div class="form-message" role="alert" hidden></div>
  <div class="falcon-tabs ds-tabs" role="tablist">${DS_TABS.map(([k, l]) => `<button type="button" role="tab" class="falcon-tab ${k === dsTab ? "active" : ""}" aria-selected="${k === dsTab}" data-action="ds-tab" data-tab="${k}">${esc(l)}${k === "page2" ? ` <span class="ds-tab-badge ${p2On ? "is-on" : ""}" id="ds-p2-badge">${p2On ? "On" : "Off"}</span>` : ""}</button>`).join("")}</div>
  ${pane("brand", `<div class="form-grid ds-grid">
    <div class="form-group"><label>Default brand <span class="muted">(for garments without one)</span></label><select name="defaultBrand" ${ro}><option value="">First brand in the list</option>${design.brands.map((b) => `<option ${b === s.defaultBrand ? "selected" : ""}>${esc(b)}</option>`).join("")}</select></div>
    <div class="form-group"><label>Colours from</label><select name="themeMode" ${ro}><option value="brand" ${s.themeMode === "brand" ? "selected" : ""}>Each garment's brand</option><option value="custom" ${s.themeMode === "custom" ? "selected" : ""}>These colours</option></select></div>
    <div class="form-group full"><label>Page colours</label><div class="ds-colours">${colour("primaryColor", "Primary", s.primaryColor)}${colour("secondaryColor", "Secondary", s.secondaryColor)}</div><small>Used when "Colours from" is "These colours", or when a brand has none (Settings → Database Lists → Brand).</small></div>
    <div class="form-group full"><label>Footer text <span class="muted">(empty: the brand's footer text, or BUILT FOR PERFORMANCE)</span></label><input name="footerText" maxlength="200" value="${esc(s.footerText)}" ${ro}></div>
    <div class="form-group full"><label>Fabric details</label><select name="fabricDisplay" ${ro}><option value="code" ${s.fabricDisplay === "code" ? "selected" : ""}>Fabric code only</option>${composition || s.fabricDisplay === "full" ? `<option value="full" ${s.fabricDisplay === "full" ? "selected" : ""}>Fabric code plus full composition and technical details</option>` : ""}</select><small>Each garment can override this on its Details tab.</small></div>
  </div>`)}
  ${pane("boxes", `<div class="ds-sub">Box colours</div><div class="ds-colours">${DATASHEET_COLOURS.map(([k, l]) => colour(String(k), l, String(s[k]))).join("")}</div>
  <div class="ds-sub">Font sizes <span class="muted">points</span></div><div class="form-grid ds-grid">${DATASHEET_FONTS.map(([k, l, , min, max]) => num(k, l, min, max, "pt")).join("")}</div>`)}
  ${pane("spacing", `<div class="ds-sub">Space after each box</div>${DATASHEET_SPACERS.map(([k, l, , max]) => slider(k, l.replace(/^After /, ""), max)).join("")}<small class="muted ds-note">Standards &amp; Certifications always sit just above the footer.</small>`)}
  ${pane("page2", `<label class="check-row ds-p2-on"><input type="checkbox" id="ds-p2-enabled" ${s.page2.enabled ? "checked" : ""} ${ro}><span><strong>Add a second page to every datasheet</strong><small class="muted">Same header, logo and footer as page 1. Between them: your own pictures and text boxes, placed where you want.</small></span></label>
  <div id="ds-p2-editor">${p2EditorHtml(ro)}</div>
  <div id="ds-p2-fit" class="ds-p2-fit" hidden></div>`)}
  ${design.canEdit ? `<div class="form-actions falcon-sticky-actions"><span id="ds-status" class="falcon-save-status">${savedText()}</span><button type="button" class="secondary" data-action="ds-reset">Undo changes</button><button type="submit" class="primary">Save</button></div>` : ""}
</form>
<div class="falcon-ds-preview">
  <div class="falcon-ds-preview-head"><label>Preview with <select id="ds-sample">${design.garments.map((g) => `<option value="${esc(g.id)}" ${g.id === design!.sample?.product.id ? "selected" : ""}>${esc(g.code)} · ${esc(g.name)}</option>`).join("")}</select></label><span class="muted" id="ds-pages-note"></span></div>
  <div class="falcon-ds-pages" id="ds-preview">${design.sample ? datasheetPagesHtml(design.sample, s, fileOf, "falcon-sheet-small", !!design.canEdit) : '<div class="empty">Add a garment to see a preview.</div>'}</div>
</div>
</div>`);
  afterPreview();
}

/** The second page's parts: text boxes (a heading and the text) and pictures. They are placed and sized by dragging
 *  them on the preview; here their words are written. A part later in the list sits on top of earlier ones. */
function p2EditorHtml(ro = design?.canEdit ? "" : "disabled"): string {
  const rows = p2Blocks.map((b, i) => {
    const tools = ro ? "" : `<div class="ds-p2-tools"><button type="button" class="icon-btn ds-p2-remove" data-action="ds-p2-remove" data-i="${i}" aria-label="Remove" title="Remove">✕</button></div>`;
    if (b.type === "text") return `<div class="ds-p2-block" data-i="${i}" data-type="text"><div class="ds-p2-head"><strong>Text box</strong>${tools}</div>
<input data-p2="heading" maxlength="120" placeholder="Heading (optional), e.g. ABOUT US" value="${esc(b.heading)}" ${ro} aria-label="Heading">
<textarea data-p2="text" rows="4" maxlength="4000" placeholder="Write here. Start a line with - for a bullet point. **Two stars** make words bold." ${ro} aria-label="Text">${esc(b.text)}</textarea></div>`;
    return `<div class="ds-p2-block" data-i="${i}" data-type="image" data-file="${esc(b.fileId)}"><div class="ds-p2-head"><strong>Picture</strong>${tools}</div>
<div class="ds-p2-pic"><img src="${fileOf(b.fileId)}" alt=""><div class="ds-p2-pic-fields"><input data-p2="caption" maxlength="200" placeholder="Caption under the picture (optional)" value="${esc(b.caption)}" ${ro} aria-label="Caption"></div></div></div>`;
  }).join("");
  const full = p2Blocks.length >= DATASHEET_PAGE2_MAX_BLOCKS;
  return `${ro ? "" : '<p class="ds-p2-how"><strong>Place them on the preview:</strong> drag a picture or text box to move it, and drag its bottom-right corner to make it bigger or smaller.</p>'}
${rows || '<div class="empty ds-p2-empty">Nothing on page 2 yet. Add a picture or a text box.</div>'}
${ro ? "" : `<div class="ds-p2-add"><label class="secondary ds-file-btn ${full ? "is-disabled" : ""}">+ Picture<input type="file" accept="image/png,image/jpeg,image/webp" id="ds-p2-file" hidden ${full ? "disabled" : ""}></label><button type="button" class="secondary" data-action="ds-p2-add-text" ${full ? "disabled" : ""}>+ Text box</button><button type="button" class="link-btn" data-action="ds-p2-tidy" ${p2Blocks.length ? "" : "disabled"}>Line them up again</button><small class="muted">${full ? `Up to ${DATASHEET_PAGE2_MAX_BLOCKS} parts.` : "Pictures: PNG, JPG or WebP up to 5 MB."}</small></div>`}`;
}

/** The parts as they are on screen now: the words from the boxes above, the place and size from dragging. */
function p2FromScreen(): DatasheetBlock[] {
  return Array.from(document.querySelectorAll<HTMLElement>("#ds-p2-editor .ds-p2-block")).map((el, i): DatasheetBlock => {
    const v = (k: string) => el.querySelector<HTMLInputElement>(`[data-p2="${k}"]`)?.value ?? "";
    const box = { x: p2Blocks[i]?.x ?? 0, y: p2Blocks[i]?.y ?? 0, w: p2Blocks[i]?.w ?? 100, h: p2Blocks[i]?.h ?? 25 };
    return el.dataset.type === "image"
      ? { type: "image", fileId: el.dataset.file || "", caption: v("caption"), ...box }
      : { type: "text", heading: v("heading"), text: v("text"), ...box };
  });
}
function redrawP2() {
  const box = document.getElementById("ds-p2-editor");
  if (box) box.innerHTML = p2EditorHtml();
  refreshPreview();
}
/** The lowest point any part reaches (a new part goes just under it). */
const p2Bottom = () => p2Blocks.reduce((m, b) => Math.max(m, b.y + b.h), 0);
/** Width ÷ height of the space between header and footer (to keep a new picture's shape). */
function p2AreaRatio(): number {
  const body = document.querySelector<HTMLElement>("#ds-preview .gds-page2-body");
  const r = body?.getBoundingClientRect();
  return r && r.height > 0 ? r.width / r.height : 184 / 200;
}

/** After each preview: say how many pages there are, and warn (and stop Save) when page 2 is too full to fit. */
function afterPreview() {
  const s = draft();
  const on = s.page2.enabled && s.page2.blocks.length > 0;
  const note = document.getElementById("ds-pages-note");
  if (note) note.textContent = on ? "2 pages: scroll down for page 2" : "1 page";
  const badge = document.getElementById("ds-p2-badge");
  if (badge) { badge.textContent = on ? "On" : "Off"; badge.classList.toggle("is-on", on); }
  const fit = document.getElementById("ds-p2-fit");
  // A text box whose writing is longer than the box would be cut off: say which, and don't save.
  const parts = Array.from(document.querySelectorAll<HTMLElement>("#ds-preview .gds-p2-text"));
  const full = parts.filter((p) => { const c = p.querySelector<HTMLElement>(".gds-p2-copy"); return !!c && c.scrollHeight > c.clientHeight + 1; });
  parts.forEach((p) => p.classList.toggle("gds-p2-over", full.includes(p)));
  const over = full.length > 0;
  if (fit) {
    fit.hidden = !on;
    fit.className = `ds-p2-fit ${over ? "is-over" : "is-ok"}`;
    const fix = "Make the box bigger by dragging its corner, or shorten the writing. It can't be saved like this.";
    fit.textContent = !over ? "Everything on page 2 fits." : full.length === 1 ? `A text box is too small for its writing (shown in red on the preview). ${fix}` : `${full.length} text boxes are too small for their writing (shown in red on the preview). ${fix}`;
  }
  const save = document.querySelector<HTMLButtonElement>('form[data-form="datasheet-settings"] button[type=submit]');
  if (save) save.dataset.over = over ? "1" : "";
  // Pictures change the height once they have loaded.
  document.querySelectorAll<HTMLImageElement>("#ds-preview .gds-page2-body img").forEach((img) => { if (!img.complete) img.addEventListener("load", () => afterPreview(), { once: true }); });
}

onAction("ds-tab", (el) => {
  dsTab = el.dataset.tab || "brand";
  document.querySelectorAll<HTMLElement>(".ds-tabs .falcon-tab").forEach((t) => { const on = t.dataset.tab === dsTab; t.classList.toggle("active", on); t.setAttribute("aria-selected", String(on)); });
  document.querySelectorAll<HTMLElement>(".ds-pane").forEach((p) => { p.hidden = p.dataset.pane !== dsTab; });
  if (dsTab === "page2") document.querySelector<HTMLElement>("#ds-preview .gds-page2")?.closest<HTMLElement>(".falcon-sheet-frame")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
});
onAction("ds-p2-add-text", () => {
  p2Blocks = p2FromScreen();
  if (p2Blocks.length >= DATASHEET_PAGE2_MAX_BLOCKS) return;
  const h = 25, y = Math.min(p2Bottom() + (p2Blocks.length ? 2 : 0), 100 - h);
  p2Blocks.push({ type: "text", heading: "", text: "", x: 0, y, w: 100, h });
  const tick = document.getElementById("ds-p2-enabled") as HTMLInputElement | null;
  if (tick && p2Blocks.length === 1) tick.checked = true;
  redrawP2();
  const areas = document.querySelectorAll<HTMLTextAreaElement>("#ds-p2-editor textarea");
  areas[areas.length - 1]?.focus();
});
onAction("ds-p2-tidy", () => {
  // One under the other, full width, in list order (the sizes stay).
  p2Blocks = p2FromScreen();
  let y = 0;
  for (const b of p2Blocks) { b.x = 0; b.w = 100; b.h = Math.min(b.h, 100); b.y = Math.min(y, 100 - b.h); y = b.y + b.h + 2; }
  redrawP2();
});
onAction("ds-p2-remove", (el) => {
  p2Blocks = p2FromScreen();
  p2Blocks.splice(Number(el.dataset.i), 1);
  redrawP2();
});

const savedText = () => (design?.saved.updatedAt ? `Saved ${formatDateTime(design.saved.updatedAt)}${design.saved.updatedBy ? ` by ${esc(design.saved.updatedBy)}` : ""}` : "Using the standard design");

function draft(): DatasheetSettings {
  const form = document.querySelector<HTMLFormElement>('form[data-form="datasheet-settings"]');
  const out: any = { ...design!.saved };
  if (form) for (const el of Array.from(form.elements) as HTMLInputElement[]) if (el.name) out[el.name] = el.type === "number" ? Number(el.value) : el.value;
  if (form && document.getElementById("ds-p2-editor")) out.page2 = { enabled: !!(document.getElementById("ds-p2-enabled") as HTMLInputElement | null)?.checked, blocks: p2FromScreen() };
  return normalizeDatasheetSettings(out);
}
let previewTimer: number | undefined;
function refreshPreview(fetchFabric = false) {
  if (!design?.sample) return;
  const s = draft();
  const status = document.getElementById("ds-status");
  if (status) { status.textContent = JSON.stringify({ ...s, updatedAt: "", updatedBy: "" }) === JSON.stringify({ ...design.saved, updatedAt: "", updatedBy: "" }) ? savedText() : "Unsaved changes"; status.className = `falcon-save-status ${status.textContent === "Unsaved changes" ? "unsaved" : ""}`; }
  const box = document.getElementById("ds-preview");
  if (!fetchFabric) { if (box) box.innerHTML = datasheetPagesHtml(design.sample, s, fileOf, "falcon-sheet-small", !!design.canEdit); afterPreview(); return; }
  window.clearTimeout(previewTimer);
  previewTimer = window.setTimeout(async () => {
    try {
      design!.sample = await get<SheetData>(`/api/org/items/${design!.sample!.product.id}/datasheet?display=${s.fabricDisplay}`);
      if (box) box.innerHTML = datasheetPagesHtml(design!.sample!, draft(), fileOf, "falcon-sheet-small", !!design!.canEdit);
      afterPreview();
    } catch (err) { toast((err as Error).message, "error"); }
  }, 150);
}
document.addEventListener("input", (e) => {
  const t = e.target as HTMLInputElement;
  if (!t.closest('form[data-form="datasheet-settings"]')) return;
  if (t.dataset.sync) { const n = t.form?.querySelector<HTMLInputElement>(`input[type=number][name="${t.dataset.sync}"]`); if (n) n.value = t.value; }
  if (t.type === "number") { const r = t.form?.querySelector<HTMLInputElement>(`input[type=range][data-sync="${t.name}"]`); if (r) r.value = t.value; }
  refreshPreview();
});
document.addEventListener("change", async (e) => {
  const t = e.target as HTMLElement;
  if (t.id === "ds-sample" && design) {
    design.sample = await get<SheetData>(`/api/org/items/${(t as HTMLSelectElement).value}/datasheet?display=${draft().fabricDisplay}`).catch((err) => { toast((err as Error).message, "error"); return design!.sample; });
    refreshPreview();
  } else if (t.closest('form[data-form="datasheet-settings"]')) {
    refreshPreview((t as HTMLSelectElement).name === "fabricDisplay");
  }
});
onAction("ds-reset", () => renderDesign());
document.addEventListener("change", async (e) => {
  const t = e.target as HTMLInputElement;
  if (t.id !== "ds-p2-file" || !t.files?.[0]) return;
  const f = t.files[0];
  t.value = "";
  if (f.size > 5 * 1024 * 1024) { toast("Pictures must be 5 MB or smaller.", "error"); return; }
  try {
    toast("Adding the picture…");
    const r = await post<{ id: string }>("/api/org/settings/datasheet/picture", { dataUrl: await readFileAsDataUrl(f), fileName: f.name });
    p2Blocks = p2FromScreen();
    // Its shape: the picture's own width and height (60% of the page width, shorter if that is too tall).
    const img = await new Promise<HTMLImageElement>((ok) => { const i = new Image(); i.onload = i.onerror = () => ok(i); i.src = URL.createObjectURL(f); });
    const shape = img.naturalWidth && img.naturalHeight ? img.naturalHeight / img.naturalWidth : 0.6;
    let w = 60, h = Math.round(w * p2AreaRatio() * shape * 10) / 10;
    if (h > 60) { w = Math.round((w * 60) / h * 10) / 10; h = 60; }
    const y = Math.min(p2Bottom() + (p2Blocks.length ? 2 : 0), 100 - h);
    p2Blocks.push({ type: "image", fileId: r.id, caption: "", x: Math.round((100 - w) / 2 * 10) / 10, y, w, h });
    const tick = document.getElementById("ds-p2-enabled") as HTMLInputElement | null;
    if (tick && p2Blocks.length === 1) tick.checked = true;
    redrawP2();
  } catch (err) { toast((err as Error).message, "error"); }
});
// ---- Dragging parts on the page 2 preview: move, or resize from the bottom-right corner -------------------------------
let dragging: { i: number; mode: "move" | "size"; sx: number; sy: number; start: { x: number; y: number; w: number; h: number }; rw: number; rh: number; el: HTMLElement } | null = null;
const snap = (v: number) => { const r = Math.round(v * 2) / 2; for (const t of [0, 50, 100]) if (Math.abs(r - t) < 1.5) return t; return r; };
document.addEventListener("pointerdown", (e) => {
  const t = e.target as HTMLElement;
  const part = t.closest<HTMLElement>("#ds-preview [data-p2-part]");
  if (!part || !design?.canEdit || e.button > 0) return;
  const body = part.closest<HTMLElement>(".gds-page2-body")!;
  const r = body.getBoundingClientRect();
  p2Blocks = p2FromScreen();
  const i = Number(part.dataset.p2Part);
  const b = p2Blocks[i];
  if (!b) return;
  e.preventDefault();
  dragging = { i, mode: t.closest("[data-p2-resize]") ? "size" : "move", sx: e.clientX, sy: e.clientY, start: { x: b.x, y: b.y, w: b.w, h: b.h }, rw: r.width, rh: r.height, el: part };
  part.classList.add("is-dragging");
  part.setPointerCapture?.(e.pointerId);
  if (dsTab !== "page2") document.querySelector<HTMLElement>('.ds-tabs [data-tab="page2"]')?.click();
});
document.addEventListener("pointermove", (e) => {
  if (!dragging) return;
  const d = dragging, b = p2Blocks[d.i];
  const dx = ((e.clientX - d.sx) / d.rw) * 100, dy = ((e.clientY - d.sy) / d.rh) * 100;
  if (d.mode === "move") {
    b.x = Math.min(100 - b.w, Math.max(0, snap(d.start.x + dx)));
    b.y = Math.min(100 - b.h, Math.max(0, snap(d.start.y + dy)));
    // The right and bottom edges snap too.
    const right = snap(b.x + b.w); if (right === 100 || right === 50) b.x = right - b.w;
  } else {
    b.w = Math.min(100 - b.x, Math.max(10, snap(d.start.x + d.start.w + dx) - d.start.x));
    b.h = Math.min(100 - b.y, Math.max(5, snap(d.start.y + d.start.h + dy) - d.start.y));
  }
  Object.assign(d.el.style, { left: `${b.x}%`, top: `${b.y}%`, width: `${b.w}%`, height: `${b.h}%` });
});
const endDrag = () => {
  if (!dragging) return;
  dragging.el.classList.remove("is-dragging");
  dragging = null;
  refreshPreview();
};
document.addEventListener("pointerup", endDrag);
document.addEventListener("pointercancel", endDrag);
// Arrow keys nudge the part being dragged with the keyboard: Tab to it on the preview, arrows move, Shift + arrows resize.
document.addEventListener("keydown", (e) => {
  const part = (e.target as HTMLElement).closest?.<HTMLElement>("#ds-preview [data-p2-part]");
  if (!part || !design?.canEdit || !e.key.startsWith("Arrow")) return;
  e.preventDefault();
  p2Blocks = p2FromScreen();
  const b = p2Blocks[Number(part.dataset.p2Part)];
  if (!b) return;
  const dx = e.key === "ArrowLeft" ? -1 : e.key === "ArrowRight" ? 1 : 0, dy = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
  if (e.shiftKey) { b.w = Math.min(100 - b.x, Math.max(10, b.w + dx)); b.h = Math.min(100 - b.y, Math.max(5, b.h + dy)); }
  else { b.x = Math.min(100 - b.w, Math.max(0, b.x + dx)); b.y = Math.min(100 - b.h, Math.max(0, b.y + dy)); }
  const i = part.dataset.p2Part;
  refreshPreview();
  document.querySelector<HTMLElement>(`#ds-preview [data-p2-part="${i}"]`)?.focus();
});

onForm("datasheet-settings", async (form) => {
  if (!design) return;
  const status = document.getElementById("ds-status");
  if (form.querySelector<HTMLButtonElement>("button[type=submit]")?.dataset.over) {
    document.querySelector<HTMLElement>('.ds-tabs [data-tab="page2"]')?.click();
    if (status) { status.textContent = "Not saved: a text box on page 2 is too small for its writing."; status.className = "falcon-save-status error"; }
    return;
  }
  if (status) { status.textContent = "Saving…"; status.className = "falcon-save-status"; }
  const btn = form.querySelector<HTMLButtonElement>("button[type=submit]");
  if (btn) btn.disabled = true;
  try {
    const { updatedAt, updatedBy, template, ...body } = draft();
    void updatedAt; void updatedBy; void template;
    const r = await put<{ settings: DatasheetSettings }>("/api/org/settings/datasheet", body);
    design.saved = r.settings;
    if (status) { status.textContent = savedText(); status.className = "falcon-save-status saved"; }
    toast("Datasheet settings saved.");
  } catch (err) {
    if (status) { status.textContent = `Not saved: ${(err as Error).message}`; status.className = "falcon-save-status error"; }
  } finally {
    if (btn) btn.disabled = false;
  }
});

/** One garment's A4 datasheet as a PDF (as printed from its datasheet page), e.g. to attach to an email from Falcon CRM. */
export async function datasheetPdfBytes(d: SheetData, fileUrl?: (id: string | null) => string): Promise<Uint8Array> {
  const stage = document.createElement("div");
  stage.className = "bulk-datasheet-stage";
  document.body.appendChild(stage);
  try { return await sheetPdf(stage, d, fileUrl); } finally { stage.remove(); }
}

// ---- Garments page: download many datasheets as PDFs in one zip ------------------------------------------
let bulkCancelled = false;
const waitForImages = (el: HTMLElement) => Promise.all([...el.querySelectorAll("img")].map((img) => (img.complete ? Promise.resolve() : new Promise<void>((r) => { img.onload = img.onerror = () => r(); }))));

/** Build each garment's A4 datasheet as a PDF (as printed from its datasheet page) and download them as one zip. */
export async function downloadDatasheets(ids: string[], what: string): Promise<void> {
  if (!ids.length) { toast("There are no garments to download.", "error"); return; }
  bulkCancelled = false;
  openModal("Downloading datasheets", `<div class="bulk-datasheet-progress"><p id="bulk-ds-status">Preparing ${ids.length} datasheet${ids.length === 1 ? "" : "s"} (${esc(what)})…</p><progress id="bulk-ds-bar" max="${ids.length}" value="0"></progress><div class="form-actions"><button type="button" class="secondary" data-action="bulk-ds-cancel">Cancel</button></div></div>`);
  const stage = document.createElement("div");
  stage.className = "bulk-datasheet-stage";
  document.body.appendChild(stage);
  const files: Array<{ name: string; data: Uint8Array }> = [];
  const failed: string[] = [];
  try {
    for (let i = 0; i < ids.length; i++) {
      if (bulkCancelled) return;
      const status = document.getElementById("bulk-ds-status");
      try {
        const d = await get<SheetData>(`/api/org/items/${ids[i]}/datasheet`);
        if (status) status.textContent = `Datasheet ${i + 1} of ${ids.length}: ${d.product.code}`;
        files.push({ name: `${d.product.code || "Garment"} Datasheet.pdf`, data: await sheetPdf(stage, d) });
      } catch {
        failed.push(ids[i]);
      }
      const bar = document.getElementById("bulk-ds-bar") as HTMLProgressElement | null;
      if (bar) bar.value = i + 1;
    }
    if (!files.length) { toast("The datasheets couldn't be made. Try again.", "error"); return; }
    const url = URL.createObjectURL(new Blob([zip(files) as BlobPart], { type: "application/zip" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `Falcon_Datasheets_${orgToday()}.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    toast(`${files.length} datasheet${files.length === 1 ? "" : "s"} downloaded${failed.length ? ` (${failed.length} couldn't be made)` : ""}.`);
  } finally {
    stage.remove();
    closeModal();
  }
}
onAction("bulk-ds-cancel", () => { bulkCancelled = true; closeModal(); toast("Download cancelled."); });
